import { Formik } from "formik";
import { useEffect, useMemo } from "react";
import { Redirect, useHistory } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import { getLoanAgreementValidationSchema } from "./utils";

import AgreementsForm from "./agreementsForm";
import { useLoanApplicationContext } from "../../../context/LoanApplication";
import { useGlobalContext } from "../../../context/globalContext";
import {
  getAgreementsForLoanApplication,
  submitLoanApplication,
} from "../../../API/creditInfoService";
import finishLogo from "../../../assets/icons/finishLogo.PNG";
import { TextWrapper } from "../../common/styledComponents/submitStepStyles/submitStepStyles";
import successEvent from "../../../utils/helpers/successEvent";
import Loader from "../../../shared/loaders/simpleLoader";
import { ROUTES } from "../../piOnboarding/steps/constants";
import {
  getClientTypeIdFromOnboardingToken,
  onboardingPath,
} from "../../piOnboarding/steps/helpers";
import { SERVICE_TYPES } from "../../../utils/constants/fieldErrors";

const Agreements = ({ onCompleted }) => {
  const { t } = useTranslation();
  const {
    agreementValues,
    agreements,
    setAgreements,
    setAgreementValues,
  } = useLoanApplicationContext();
  const history = useHistory();
  const { getValues } = useFormContext();
  const { setErrorMessage, setShown } = useGlobalContext();
  const onboardingToken = sessionStorage.getItem("onboardingToken");

  const initialValues = useMemo(() => ({ ...agreementValues }), [agreementValues]);

  const submission = useMutation({
    mutationFn: submitLoanApplication,
    onSuccess: () => {
      onCompleted();
      document.dispatchEvent(successEvent);
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.Message ?? error.message);
      setShown(true);
    },
  });

  const { isSuccess, mutate: loadAgreements } = useMutation({
    mutationFn: () => getAgreementsForLoanApplication(),
    onSuccess: (response) => {
      setAgreements(response.data);
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.Message ?? error.message);
      setShown(true);
      history.replace(onboardingPath(ROUTES.privateInformation));
    },
  });

  useEffect(() => {
    if (onboardingToken) {
      loadAgreements();
    }
  }, [loadAgreements, onboardingToken]);

  if (submission.isSuccess) {
    return (
      <div>
        <div className="flex justify-center items-center">
          <img src={finishLogo} alt="" />
        </div>
        <TextWrapper className="textsWrapper">
          <span className="title">{t("completedSuccessfully")}</span>
          <div>{t("finishText")}</div>
          <div>{t("waitForMessage")}</div>
        </TextWrapper>
      </div>
    );
  }

  if (!onboardingToken) {
    return <Redirect to={onboardingPath(ROUTES.identification)} />;
  }

  if (!isSuccess) return <Loader />;

  function prevStep() {
    history.push(onboardingPath(ROUTES.privateInformation));
  }

  async function handleSubmit(consents) {
    if (submission.isPending) return;
    const sendForm = {
      additionalInfo: {
        identomatSessionToken: localStorage.getItem("sessionToken"),
        ServiceType: SERVICE_TYPES.loanApplication,
        client_type: getClientTypeIdFromOnboardingToken(),
        locale: localStorage.getItem("culture"),
      },
      formValues: { ...getValues(), ...consents },
    };
    try {
      await submission.mutateAsync(sendForm);
    } catch {
      // The mutation shows the error while preserving the form for retry.
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={getLoanAgreementValidationSchema(t("required"))}
      onSubmit={handleSubmit}
      validateOnBlur
    >
      <AgreementsForm
        agreements={agreements}
        setAgreementValues={setAgreementValues}
        prevStep={prevStep}
      />
    </Formik>
  );
};

export default Agreements;
